import React, { useState } from 'react';
import { CheckCircle, AlertCircle, List, FileText, X } from 'lucide-react';
import MermaidFlowchart from './MermaidFlowchart';

/**
 * 签证助手 - 右侧输出面板
 * 实时显示：
 * 1. 免面谈判断结果
 * 2. EVUS提醒
 * 3. 办理流程步骤
 * 4. 材料清单分组
 * 5. 免责声明
 */
const AssistantOutputPanel = ({ computedResults, userInputs, onNavigate }) => {
  // 🆕 流程图视图切换状态
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'flowchart'
  // 🆕 全屏 Modal 状态
  const [isModalOpen, setIsModalOpen] = useState(false);
  const getColorScheme = (color) => {
    const schemes = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-700',
        badge: 'bg-blue-100 text-blue-800'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-700',
        badge: 'bg-green-100 text-green-800'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-700',
        badge: 'bg-purple-100 text-purple-800'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        badge: 'bg-amber-100 text-amber-800'
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        badge: 'bg-red-100 text-red-800'
      }
    };
    return schemes[color] || schemes.blue;
  };

  // 获取步骤编号颜色
  const getStepNumberClass = (highlight) => {
    return highlight
      ? 'bg-blue-600 text-white'
      : 'bg-gray-300 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* 空状态提示 */}
      {!userInputs.province && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-2xl border-2 border-dashed border-gray-200 p-6">
          <p className="text-lg mb-2">👆 请先在左侧填写您的信息</p>
          <p className="text-sm text-gray-400">右侧将实时显示您的材料清单和办理流程</p>
        </div>
      )}

      {userInputs.province && (
        <>
          {/* 免面谈判断结果 - 仅当需要申请且为续签时显示 */}
          {userInputs.visaStatus === 'need_apply' && !userInputs.isFirstTime && (
            <div
              className={`p-4 rounded-xl border-2 ${
                computedResults.canWaiveInterview
                  ? 'bg-green-50 border-green-300'
                  : 'bg-amber-50 border-amber-300'
              }`}
            >
              <div className="flex items-start gap-3">
                {computedResults.canWaiveInterview ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                )}
                <div>
                  <h4
                    className={`font-bold mb-1 ${
                      computedResults.canWaiveInterview
                        ? 'text-green-800'
                        : 'text-amber-800'
                    }`}
                  >
                    {computedResults.canWaiveInterview
                      ? '✅ 您可能符合免面谈条件'
                      : '⚠️ 您需要前往使领馆面谈'}
                  </h4>
                  <p
                    className={`text-sm ${
                      computedResults.canWaiveInterview
                        ? 'text-green-700'
                        : 'text-amber-700'
                    }`}
                  >
                    {computedResults.canWaiveInterview
                      ? '您可通过中信银行代传递或快递方式递交材料，无需亲自前往使领馆。即使符合免面谈条件，使领馆仍可能要求您进行面谈。'
                      : '请预约面谈时间，准备好所有材料，按时到达指定使领馆。'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* EVUS强提醒 */}
          {computedResults.needsEVUS && (
            <div className="p-4 rounded-xl border-2 bg-red-50 border-red-300">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-red-800 mb-2">
                    🚨 重要提醒：必须完成EVUS登记
                  </h4>
                  <p className="text-sm text-red-700 mb-3">
                    您持有10年有效期B1/B2签证，出发前必须完成EVUS登记（每2年或换护照后需重新登记）。
                    <strong>未完成EVUS登记将无法登机！</strong>
                  </p>
                  <button
                    onClick={() => onNavigate && onNavigate('/evus/enroll')}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                  >
                    立即前往EVUS登记
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 办理流程步骤 - 支持列表视图和流程图视图切换 */}
          <div className="bg-white rounded-2xl border-2 border-gray-100 p-6 shadow-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <List className="w-6 h-6 mr-2 text-blue-600" />
                办理流程步骤
              </h3>
              {/* 🆕 视图切换按钮 */}
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'list'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  列表视图
                </button>
                <button
                  onClick={() => setViewMode('flowchart')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'flowchart'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  流程图视图
                </button>
              </div>
            </div>

            {/* 列表视图 */}
            {viewMode === 'list' && (
            <div className="space-y-3">
              {computedResults.processSteps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-3 rounded-lg transition-all ${
                    step.highlight
                      ? 'bg-blue-50 border-2 border-blue-300'
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${getStepNumberClass(
                      step.highlight
                    )}`}
                  >
                    {step.step}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900">{step.title}</h4>
                      {step.badge && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                          {step.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{step.desc}</p>

                    {/* 🆕 如果有链接，显示操作按钮 */}
                    {step.link && (
                      <button
                        onClick={() => onNavigate && onNavigate(step.link)}
                        className="mt-3 inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        {step.linkText || '前往操作'}
                        <span className="ml-1">→</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}

            {/* 🆕 流程图视图 - 添加放大按钮 */}
            {viewMode === 'flowchart' && (
            <div className="relative">
              {/* 放大按钮 - 悬浮在右上角 */}
              <button
                onClick={() => setIsModalOpen(true)}
                className="absolute top-2 right-2 z-10 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
              >
                <span>🔍</span>
                <span>放大查看</span>
              </button>

              {/* 普通大小的流程图 */}
              <MermaidFlowchart isFullscreen={false} />
            </div>
            )}
          </div>

          {/* 材料清单 - 当已有签证时仅显示EVUS，否则显示完整清单 */}
          {computedResults.materialGroups.length > 0 && (
          <div className="bg-white rounded-2xl border-2 border-gray-100 p-6 shadow-md">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <FileText className="w-6 h-6 mr-2 text-green-600" />
              {userInputs.visaStatus === 'have_visa' ? 'EVUS提醒' : '材料清单'}
            </h3>

            <div className="space-y-4">
              {computedResults.materialGroups.map((group) => {
                const colors = getColorScheme(group.color);
                return (
                  <div
                    key={group.id}
                    className={`border-2 rounded-lg p-4 ${colors.border} ${colors.bg}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${colors.badge}`}>
                        {group.color === 'blue' && <FileText className="w-5 h-5" />}
                        {group.color === 'green' && <FileText className="w-5 h-5" />}
                        {group.color === 'purple' && <FileText className="w-5 h-5" />}
                        {group.color === 'amber' && <AlertCircle className="w-5 h-5" />}
                        {group.color === 'red' && <AlertCircle className="w-5 h-5" />}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-900 ${colors.text}`}>
                          {group.title}
                        </h4>
                        <p className="text-xs text-gray-600">{group.subtitle}</p>
                      </div>
                    </div>

                    <ul className="space-y-2 ml-8">
                      {group.items.map((item, idx) => (
                        <li
                          key={idx}
                          className={`flex items-start gap-2 text-sm ${
                            item.highlight
                              ? 'text-gray-900 font-medium'
                              : 'text-gray-700'
                          }`}
                        >
                          <span
                            className={`flex-shrink-0 mt-1 ${
                              item.highlight
                                ? 'text-blue-600'
                                : 'text-gray-400'
                            }`}
                          >
                            {item.highlight ? '🔹' : '▫️'}
                          </span>
                          <span>{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* 免责声明 */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
            <p className="mb-3 font-medium">
              <strong>📌 免责声明：</strong>
            </p>
            <ul className="space-y-1 text-xs text-gray-600">
              <li>
                • 本助手提供的信息仅供参考，具体要求请以美国国务院官网（
                <a
                  href="https://travel.state.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  travel.state.gov
                </a>
                ）和各领事馆最新公告为准
              </li>
              <li>• 签证申请结果由使领馆签证官根据您的实际情况判定，本系统无法保证签证通过率</li>
              <li>
                • 即使符合免面谈条件，使领馆仍可能出于任何原因要求您进行面谈
              </li>
              <li>
                • 不再按申请人当前年龄（如小于14岁或大于79岁）自动免面谈
              </li>
              <li>
                • B类续签免面谈中唯一直接涉及年龄的条件是：上一张签证签发时申请人必须已满18岁
              </li>
            </ul>
            <p className="text-gray-500 mt-3">
              最后更新：2025年12月 | 数据来源：美国国务院领事事务局
            </p>
          </div>
        </>
      )}

      {/* 🆕 全屏 Modal - 放大查看流程图 */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative bg-white rounded-2xl w-[95vw] h-[95vh] p-6 overflow-auto flex flex-col">
            {/* 关闭按钮 */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 z-20 p-2 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors"
              aria-label="关闭"
            >
              <X className="w-6 h-6" />
            </button>

            {/* 标题 */}
            <h3 className="text-2xl font-bold text-gray-900 mb-4 pr-12">
              赴美签证申请流程图
            </h3>

            {/* 全屏流程图 */}
            <div className="flex-1 overflow-auto">
              <MermaidFlowchart isFullscreen={true} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssistantOutputPanel;
